import { getZrmUserInfo } from '@zg-rentals/clickstream';

import { trackEvent } from '../../../client/analytics';

import UserPermissionsConstants from '../../constants/UserPermissionsConstants';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

const EVENT_IDS = {
  DISPLAY_FEATURE_NOTIFICATION: '3722',
  LEARN_MORE: '3723',
  DISMISS_NOTIFICATION: '3724',
  DISMISS_ALL_NOTIFICATIONS: '3725',
} as const;

const ZRM_USER_TYPES = ['premiumCustomer', 'freeCustomer'];

const LEARN_MORE_TRIGGER_OBJECT_MAP_FROM_TITLE: Record<string, string | ((roles: Array<string>) => string)> = {
  'Introducing total prices on listings.': (roles: Array<string>) => {
    if (roles.includes(UserPermissionsConstants.USER_TYPES.MULTIFAMILY_CUSTOMER)) {
      return 'zram_properties_page';
    }

    if (ZRM_USER_TYPES.some((type) => roles.includes(type))) {
      return 'zrm_properties_page';
    }

    return '';
  },
};

// https://dataportal.zillowgroup.net/event-registry/3722
const DisplayFeatureNotification = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_type_id: EVENT_IDS.DISPLAY_FEATURE_NOTIFICATION,
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'impression',
      trigger_location_nm: 'properties',
      trigger_source_nm: 'feature_notification',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3723
const LearnMore = ({ user, notificationTitle = '' }: { user: ZrmClickstreamUser; notificationTitle?: string }) => {
  const trigger_object_nm =
    typeof LEARN_MORE_TRIGGER_OBJECT_MAP_FROM_TITLE[notificationTitle] === 'function'
      ? LEARN_MORE_TRIGGER_OBJECT_MAP_FROM_TITLE[notificationTitle](user.roles || [])
      : LEARN_MORE_TRIGGER_OBJECT_MAP_FROM_TITLE[notificationTitle];

  return trackEvent.user({
    envelope: {
      event_type_id: EVENT_IDS.LEARN_MORE,
      event_type_version_id: '2',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'properties',
      trigger_source_nm: 'learn_more',
      trigger_object_nm: trigger_object_nm || 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'click_feature_notification',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });
};

// https://dataportal.zillowgroup.net/event-registry/3724
const DismissNotification = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_type_id: EVENT_IDS.DISMISS_NOTIFICATION,
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'properties',
      trigger_source_nm: 'x_x',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'dismiss_notification',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3725
const DismissAllNotifications = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_type_id: EVENT_IDS.DISMISS_ALL_NOTIFICATIONS,
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'properties',
      trigger_source_nm: 'x_x',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'dismiss_all_notifications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

const FeatureNotificationsCSEvents = {
  DisplayFeatureNotification,
  LearnMore,
  DismissNotification,
  DismissAllNotifications,
};

// Clickstream visualizer data-eventid attributes
DisplayFeatureNotification.data_event_id = EVENT_IDS.DISPLAY_FEATURE_NOTIFICATION;
LearnMore.data_event_id = EVENT_IDS.LEARN_MORE;
DismissNotification.data_event_id = EVENT_IDS.DISMISS_NOTIFICATION;
DismissAllNotifications.data_event_id = EVENT_IDS.DISMISS_ALL_NOTIFICATIONS;

export default FeatureNotificationsCSEvents;
